/* ------------------------------------------------------------------
   Central analytics façade  –  1 file, 4 vendors
   * keeps vendor code isolated
   * avoids repetitive try/catch
   * zero-duplicate Pixel / GA initialisation (handled in loader.js)
------------------------------------------------------------------- */

import { initGA, initFacebook, sdkFailed } from './loader.js'

import * as GA from './vendors/ga4.js'
import * as MOE from './vendors/moengage.js'
import mixpanel from './vendors/mixpanel.js'

import Cookies from 'universal-cookie'
import { postMethod } from '../utils/api.js'
import { ADVERTISER_PARAMETERS_LOG } from '../constants/all.js'

import { safeExecute } from '../utils/runtime.js' // tiny helpers
import { getAppType, isAndroid } from '@/android/bridge.js'

const cookies = new Cookies()

/* ===============================================================
   1️⃣  Bootstrap  – called **once** from _app.tsx
================================================================ */
export function bootstrap({ gaId, fbPixelId }) {
  initGA(gaId)
  initFacebook(fbPixelId)
  MOE.initMoEngage() // snippet already injected in _app.tsx
}

/* ===============================================================
   2️⃣  Identity helpers
================================================================ */
export function setUser(id, mobile) {
  if (!id) return

  GA.setUser(id, mobile) // GA vendor already guarded

  safeExecute(() => {
    mixpanel.identify(id)
    if (mobile) mixpanel.people.set({ mobileNumber: mobile })
  })

  safeExecute(() => MOE.identify(id, mobile))

  /* FB Advanced Matching */
  safeExecute(() => {
    if (typeof fbq !== 'undefined' && mobile) {
      fbq('init', process.env.NEXT_PUBLIC_FB_ID, { ph: String(mobile) })
      if (process.env.NEXT_PUBLIC_FB_ID_SEEKER) {
        fbq('init', process.env.NEXT_PUBLIC_FB_ID_SEEKER, { ph: String(mobile) })
      }
    }
  })
}

/** Convenience wrapper used across the code-base */
export function setAnalyticsIdentity({ jobsId, mobile }) {
  if (jobsId) setUser(String(jobsId), mobile || '')
}

export function logout() {
  GA.clearUser()
  safeExecute(() => MOE.logout())
  safeExecute(() => mixpanel.reset())
}

/* ===============================================================
   3️⃣  Main event pipeline  – called everywhere
================================================================ */
const UTM_KEYS = new Set(['utm_source', 'utm_medium', 'utm_campaign'])
const pickUtm = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).filter(([k]) => UTM_KEYS.has(k)))

export function trackEvent({
  name,
  options = {},
  searchParams = {},
  isSeeker = false,
  isJobPostedSuccessfully = false
}) {
  /* ---------- final param bag ---------- */
  const params = {
    Platform: getAppType(),
    ...pickUtm(searchParams),
    ...options
  }

  /* ---------- Android bridge ---------- */
  safeExecute(() => {
    if (isAndroid() && Android.logAnalyticsEvent) {
      Android.logAnalyticsEvent(name, params?.category || 'tap', JSON.stringify(params))
    }
  })

  /* ---------- Web / SDKs ---------- */
  const senders = [
    () => !sdkFailed.GA4 && GA.track?.(name, params),
    () => !sdkFailed.FBPX && typeof fbq !== 'undefined' && fbq('track', name, params),
    () => !isSeeker && mixpanel.track?.(name, params),
    () => MOE.track(name, params)
  ]
  senders.forEach((fn) => safeExecute(fn))

  /* ---------- backend log on job-post success ---------- */
  if (isJobPostedSuccessfully) {
    const payload = {
      name,
      ...params,
      extra: {
        google_client_id: cookies.get('_ga') || null,
        meta_fbp: cookies.get('_fbp') || undefined,
        meta_fbc: cookies.get('_fbc') || undefined
      }
    }
    safeExecute(() =>
      postMethod({ url: ADVERTISER_PARAMETERS_LOG, data: payload, advParameterFlag: true })
    )
  }
}
